import { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import useStore from './store/useStore'
import { supabase, getCurrentUser, getSession } from './services/supabase'
import AuthPage from './pages/AuthPage'
import Dashboard from './pages/Dashboard'
import OnboardingChat from './pages/OnboardingChat'
import RoadmapViewer from './pages/RoadmapViewer'
import CommunityRoadmaps from './pages/CommunityRoadmaps'
import RoadmapExchange from './pages/RoadmapExchange'
import Footer from './components/Footer'

function App() {
  const { setUser, setSession, user } = useStore()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth initialization timeout - proceeding anyway')
      setIsLoading(false)
    }, 1000) // 1 second timeout - fast page load

    // Check for existing session
    const initAuth = async () => {
      console.log('🔐 Initializing auth...', { supabaseConfigured: !!supabase })
      try {
        if (supabase) {
          const { session } = await getSession()
          console.log('📝 Session check result:', { hasSession: !!session })
          if (session) {
            setSession(session)
            const { user } = await getCurrentUser()
            setUser(user)
            console.log('✅ User authenticated:', user?.email)
          } else {
            console.log('❌ No active session')
          }
        } else {
          console.warn('⚠️ Supabase not configured - skipping auth')
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error)
      } finally {
        clearTimeout(loadingTimeout)
        setIsLoading(false)
        console.log('✅ Auth initialization complete')
      }
    }

    initAuth()

    // Listen for auth changes
    if (supabase) {
      try {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          setSession(session)
          if (session) {
            const { user } = await getCurrentUser()
            setUser(user)
          } else {
            setUser(null)
          }
        })

        return () => {
          clearTimeout(loadingTimeout)
          subscription?.unsubscribe()
        }
      } catch (error) {
        console.error('Auth state change error:', error)
      }
    }

    return () => clearTimeout(loadingTimeout)
  }, [setUser, setSession])

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    )
  }

  const isAuthenticated = !!user

  return (
    <Router>
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow">
          <Routes>
            <Route 
              path="/" 
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <AuthPage />} 
            />
            <Route 
              path="/auth" 
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <AuthPage />} 
            />
            <Route 
              path="/dashboard" 
              element={isAuthenticated ? <Dashboard /> : <Navigate to="/" />} 
            />
            <Route 
              path="/onboarding" 
              element={isAuthenticated ? <OnboardingChat /> : <Navigate to="/" />} 
            />
            <Route 
              path="/roadmap/:id" 
              element={isAuthenticated ? <RoadmapViewer /> : <Navigate to="/" />} 
            />
            <Route 
              path="/community" 
              element={isAuthenticated ? <CommunityRoadmaps /> : <Navigate to="/" />} 
            />
            <Route 
              path="/exchange" 
              element={<RoadmapExchange />} 
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  )
}

export default App
